import React, { useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Button,
  Spinner,
  Divider,
  Flex,
  Box,
  Heading,
  VStack,
  SimpleGrid,
  Checkbox,
  Input,
  Select,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  fetchFilterOptions,
  type BulkUpdateTrackField,
  type BulkUpdateTracksBody,
} from '../../../api/libraryApi';
import { LicenseSelect } from './LicenseSelect';
import { AutocompleteInput } from './AutocompleteInput';
import { TagMultiSelect } from '../../../components/forms/TagMultiSelect';

export interface BulkEditTracksModalProps {
  /** Whether the modal is open. */
  open: boolean;
  onClose: () => void;
  /** Track content ids the bulk edit applies to. */
  trackIds: string[];
  /**
   * Apply the edit. Receives the opt-in field mask and the value payload for
   * exactly those fields. The caller wires this to `bulkUpdateTracks`.
   */
  onApply: (
    fields: BulkUpdateTrackField[],
    values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>,
  ) => void;
  /** True while the bulk request is in flight. */
  isApplying?: boolean;
  /**
   * Context label for where the edit was launched from (e.g. "this playlist").
   * Purely cosmetic — affects only the affected-count helper text. Optional.
   */
  sourceLabel?: string;
}

/** Per-field opt-in flags. Only enabled fields are sent in the `fields` mask. */
interface EnabledFlags {
  artist: boolean;
  genre: boolean;
  mood: boolean;
  tempo: boolean;
  album: boolean;
  tags: boolean;
  licenseId: boolean;
  rightsHolder: boolean;
  rightsTerritory: boolean;
  contract: boolean;
  rightsHolderTrackId: boolean;
  rightsValidFrom: boolean;
  rightsValidTo: boolean;
  status: boolean;
}

const INITIAL_ENABLED: EnabledFlags = {
  artist: false,
  genre: false,
  mood: false,
  tempo: false,
  album: false,
  tags: false,
  licenseId: false,
  rightsHolder: false,
  rightsTerritory: false,
  contract: false,
  rightsHolderTrackId: false,
  rightsValidFrom: false,
  rightsValidTo: false,
  status: false,
};

/**
 * One field row: opt-in checkbox + the field control (dimmed until checked).
 * Defined at MODULE scope (not inside the modal component) so its identity is stable
 * across renders — otherwise the controlled inputs inside (genre/mood/tempo) remount
 * and lose focus on every keystroke.
 */
function FieldRow({
  fieldKey,
  label,
  enabled,
  toggle,
  children,
}: {
  fieldKey: keyof EnabledFlags;
  label: string;
  enabled: EnabledFlags;
  toggle: (key: keyof EnabledFlags) => void;
  children: React.ReactNode;
}) {
  const checked = enabled[fieldKey];
  return (
    <Box>
      <Checkbox
        isChecked={checked}
        onChange={() => toggle(fieldKey)}
        mb={2}
        fontWeight="600"
        data-testid={`bulk-edit-enable-${fieldKey}`}
      >
        {label}
      </Checkbox>
      <Box opacity={checked ? 1 : 0.4} pointerEvents={checked ? 'auto' : 'none'}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Bulk metadata editor (FR-076–FR-078). Each editable field has an opt-in
 * checkbox; only checked fields are written to every selected track — unchecked
 * fields are left untouched. Tags use REPLACE semantics (the supplied list
 * becomes each track's whole tag set), made explicit in the UI. The modal shows
 * the affected-track count and requires an explicit confirm step before applying,
 * noting that changes apply to each track everywhere it is used.
 */
export function BulkEditTracksModal({
  open,
  onClose,
  trackIds,
  onApply,
  isApplying,
  sourceLabel,
}: BulkEditTracksModalProps) {
  const { t } = useTranslation();

  const [enabled, setEnabled] = useState<EnabledFlags>(INITIAL_ENABLED);
  const [showConfirm, setShowConfirm] = useState(false);

  // Field values
  const [artist, setArtist] = useState('');
  const [genre, setGenre] = useState('');
  const [mood, setMood] = useState('');
  const [tempo, setTempo] = useState('');
  const [album, setAlbum] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const [licenseName, setLicenseName] = useState<string | null>(null);
  const [rightsHolder, setRightsHolder] = useState('');
  const [rightsTerritory, setRightsTerritory] = useState('');
  const [contract, setContract] = useState('');
  const [rightsHolderTrackId, setRightsHolderTrackId] = useState('');
  const [rightsValidFrom, setRightsValidFrom] = useState('');
  const [rightsValidTo, setRightsValidTo] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const { data: filterOptions } = useQuery({
    queryKey: ['admin-library-filter-options'],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const affectedCount = trackIds.length;

  // The opt-in field mask derived from the checkboxes.
  const selectedFields = useMemo<BulkUpdateTrackField[]>(() => {
    const fields: BulkUpdateTrackField[] = [];
    (Object.keys(enabled) as (keyof EnabledFlags)[]).forEach((key) => {
      if (enabled[key]) fields.push(key as BulkUpdateTrackField);
    });
    return fields;
  }, [enabled]);

  const hasAnyField = selectedFields.length > 0;

  const reset = () => {
    setEnabled(INITIAL_ENABLED);
    setShowConfirm(false);
    setArtist('');
    setGenre('');
    setMood('');
    setTempo('');
    setAlbum('');
    setTags([]);
    setLicenseId(null);
    setLicenseName(null);
    setRightsHolder('');
    setRightsTerritory('');
    setContract('');
    setRightsHolderTrackId('');
    setRightsValidFrom('');
    setRightsValidTo('');
    setStatus('active');
  };

  const handleClose = () => {
    if (isApplying) return;
    reset();
    onClose();
  };

  const toggle = (key: keyof EnabledFlags) => {
    setEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Build the value payload for ONLY the enabled fields. Tags replace; empty text
  // is sent as "" (the user explicitly opted in to overwrite that field).
  const buildValues = (): Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'> => {
    const values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'> = {};
    if (enabled.artist) values.artist = artist;
    if (enabled.genre) values.genre = genre;
    if (enabled.mood) values.mood = mood;
    if (enabled.tempo) values.tempo = tempo;
    if (enabled.album) values.album = album;
    if (enabled.tags) values.tags = tags;
    if (enabled.licenseId) values.licenseId = licenseId;
    if (enabled.rightsHolder) values.rightsHolder = rightsHolder;
    if (enabled.rightsTerritory) values.rightsTerritory = rightsTerritory;
    if (enabled.contract) values.contract = contract;
    if (enabled.rightsHolderTrackId) values.rightsHolderTrackId = rightsHolderTrackId;
    if (enabled.rightsValidFrom) values.rightsValidFrom = rightsValidFrom;
    if (enabled.rightsValidTo) values.rightsValidTo = rightsValidTo;
    if (enabled.status) values.status = status;
    return values;
  };

  const handleApply = () => {
    if (!hasAnyField) return;
    onApply(selectedFields, buildValues());
  };

  // Human-readable preview of the value each enabled field will be set to, shown
  // in the review step. An empty string means the field will be cleared.
  const previewValue = (field: BulkUpdateTrackField): string => {
    switch (field) {
      case 'artist': return artist;
      case 'genre': return genre;
      case 'mood': return mood;
      case 'tempo': return tempo;
      case 'album': return album;
      case 'tags': return tags.join(', ');
      case 'licenseId': return licenseName ?? '';
      case 'rightsHolder': return rightsHolder;
      case 'rightsTerritory': return rightsTerritory;
      case 'contract': return contract;
      case 'rightsHolderTrackId': return rightsHolderTrackId;
      case 'rightsValidFrom': return rightsValidFrom;
      case 'rightsValidTo': return rightsValidTo;
      case 'status':
        return status === 'active'
          ? t('admin.library.statusActive')
          : t('admin.library.statusInactive');
      default: return '';
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} isCentered scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent data-testid="bulk-edit-tracks-modal" maxW="720px" borderRadius="15px">
        <ModalHeader>
          <Heading as="h3" size="md">
            {t('admin.library.bulkEdit.title')}
          </Heading>
          <Text color="gray.600" fontSize="sm" fontWeight="400" mt={1} data-testid="bulk-edit-affected-count">
            {t('admin.library.bulkEdit.affectedCount', {
              count: affectedCount,
              context: sourceLabel,
            })}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={isApplying} />

        <ModalBody>
          {!showConfirm ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={5}>
              <Box gridColumn={{ md: '1 / -1' }}>
                <Text color="gray.600" fontSize="sm">
                  {t('admin.library.bulkEdit.subtitle')}
                </Text>
              </Box>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="artist" label={t('admin.library.artist')}>
                <Input
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  isDisabled={!enabled.artist}
                  data-testid="bulk-edit-artist"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="genre" label={t('admin.library.genre')}>
                <AutocompleteInput
                  value={genre}
                  onChange={setGenre}
                  suggestions={filterOptions?.genres ?? []}
                  isDisabled={!enabled.genre}
                  data-testid="bulk-edit-genre"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="mood" label={t('admin.library.mood')}>
                <AutocompleteInput
                  value={mood}
                  onChange={setMood}
                  suggestions={filterOptions?.moods ?? []}
                  isDisabled={!enabled.mood}
                  data-testid="bulk-edit-mood"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="tempo" label={t('admin.library.tempo')}>
                <AutocompleteInput
                  value={tempo}
                  onChange={setTempo}
                  suggestions={filterOptions?.tempos ?? []}
                  isDisabled={!enabled.tempo}
                  data-testid="bulk-edit-tempo"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="album" label={t('admin.library.album')}>
                <Input
                  value={album}
                  onChange={(e) => setAlbum(e.target.value)}
                  isDisabled={!enabled.album}
                  data-testid="bulk-edit-album"
                />
              </FieldRow>

              <Box gridColumn={{ md: '1 / -1' }}>
                <FieldRow enabled={enabled} toggle={toggle} fieldKey="tags" label={t('admin.library.tags')}>
                  <TagMultiSelect
                    value={tags}
                    onChange={setTags}
                    suggestions={filterOptions?.tags ?? []}
                    wrapInFormControl={false}
                    size="md"
                    isDisabled={!enabled.tags}
                    inputTestId="bulk-edit-tags-input"
                  />
                  <Text color="orange.600" fontSize="xs" mt={1} data-testid="bulk-edit-tags-replace-note">
                    {t('admin.library.bulkEdit.tagsReplaceNote')}
                  </Text>
                </FieldRow>
              </Box>

              <Box gridColumn={{ md: '1 / -1' }}>
                <Divider />
                <Heading size="xs" color="gray.600" mt={5}>
                  {t('admin.library.licensingHeading')}
                </Heading>
              </Box>

              <Box gridColumn={{ md: '1 / -1' }}>
                <FieldRow enabled={enabled} toggle={toggle} fieldKey="licenseId" label={t('admin.library.license')}>
                  <LicenseSelect
                    value={licenseName}
                    onChange={(id, name) => {
                      setLicenseId(id);
                      setLicenseName(name);
                    }}
                    wrapInFormControl={false}
                    isDisabled={!enabled.licenseId}
                    inputTestId="bulk-edit-license-input"
                  />
                </FieldRow>
              </Box>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="rightsHolder" label={t('admin.library.rightsHolder')}>
                <Input
                  value={rightsHolder}
                  onChange={(e) => setRightsHolder(e.target.value)}
                  isDisabled={!enabled.rightsHolder}
                  data-testid="bulk-edit-rights-holder"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="rightsTerritory" label={t('admin.library.rightsTerritory')}>
                <Input
                  value={rightsTerritory}
                  onChange={(e) => setRightsTerritory(e.target.value)}
                  isDisabled={!enabled.rightsTerritory}
                  data-testid="bulk-edit-rights-territory"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="contract" label={t('admin.library.contract')}>
                <Input
                  value={contract}
                  onChange={(e) => setContract(e.target.value)}
                  isDisabled={!enabled.contract}
                  data-testid="bulk-edit-contract"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="rightsHolderTrackId" label={t('admin.library.rightsHolderTrackId')}>
                <Input
                  value={rightsHolderTrackId}
                  onChange={(e) => setRightsHolderTrackId(e.target.value)}
                  isDisabled={!enabled.rightsHolderTrackId}
                  data-testid="bulk-edit-rights-holder-track-id"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="rightsValidFrom" label={t('admin.library.rightsValidFrom')}>
                <Input
                  type="date"
                  value={rightsValidFrom}
                  onChange={(e) => setRightsValidFrom(e.target.value)}
                  isDisabled={!enabled.rightsValidFrom}
                  data-testid="bulk-edit-rights-valid-from"
                />
              </FieldRow>

              <FieldRow enabled={enabled} toggle={toggle} fieldKey="rightsValidTo" label={t('admin.library.rightsValidTo')}>
                <Input
                  type="date"
                  value={rightsValidTo}
                  onChange={(e) => setRightsValidTo(e.target.value)}
                  isDisabled={!enabled.rightsValidTo}
                  data-testid="bulk-edit-rights-valid-to"
                />
              </FieldRow>

              <Box gridColumn={{ md: '1 / -1' }}>
                <Divider mb={5} />
                <FieldRow enabled={enabled} toggle={toggle} fieldKey="status" label={t('admin.library.status')}>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
                    isDisabled={!enabled.status}
                    data-testid="bulk-edit-status"
                  >
                    <option value="active">{t('admin.library.statusActive')}</option>
                    <option value="inactive">{t('admin.library.statusInactive')}</option>
                  </Select>
                </FieldRow>
              </Box>
            </SimpleGrid>
          ) : (
            <VStack spacing={4} align="stretch" data-testid="bulk-edit-confirm-step">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('admin.library.bulkEdit.confirmEverywhere')}
                </Text>
              </Alert>
              <Text fontSize="sm">
                {t('admin.library.bulkEdit.confirmBody', { count: affectedCount })}
              </Text>
              <Box>
                <Text fontSize="sm" fontWeight="600" mb={1}>
                  {t('admin.library.bulkEdit.confirmFieldsHeading')}
                </Text>
                <VStack spacing={1.5} align="stretch">
                  {selectedFields.map((f) => {
                    const value = previewValue(f);
                    const isEmpty = value.trim().length === 0;
                    const display = isEmpty
                      ? f === 'licenseId'
                        ? t('admin.library.bulkEdit.valueNoLicense')
                        : t('admin.library.bulkEdit.valueCleared')
                      : value;
                    return (
                      <Flex key={f} gap={2} align="baseline" data-testid={`bulk-edit-confirm-field-${f}`}>
                        <Text fontSize="sm" color="gray.600" fontWeight="500" flexShrink={0}>
                          {t(`admin.library.bulkEdit.fields.${f}`)}:
                        </Text>
                        <Text
                          fontSize="sm"
                          color={isEmpty ? 'gray.400' : 'gray.800'}
                          fontStyle={isEmpty ? 'italic' : 'normal'}
                          noOfLines={2}
                          wordBreak="break-word"
                          data-testid={`bulk-edit-confirm-value-${f}`}
                        >
                          {display}
                        </Text>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <Divider />

        <ModalFooter>
          {!showConfirm ? (
            <Flex justify="space-between" align="center" w="100%">
              <Button
                variant="outline"
                onClick={handleClose}
                isDisabled={isApplying}
                data-testid="bulk-edit-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="brand"
                onClick={() => setShowConfirm(true)}
                isDisabled={!hasAnyField || affectedCount === 0}
                data-testid="bulk-edit-review"
              >
                {t('admin.library.bulkEdit.review')}
              </Button>
            </Flex>
          ) : (
            <Flex justify="space-between" align="center" w="100%">
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                isDisabled={isApplying}
                data-testid="bulk-edit-back"
              >
                {t('common.back')}
              </Button>
              <Button
                variant="brand"
                onClick={handleApply}
                isDisabled={!hasAnyField || isApplying}
                data-testid="bulk-edit-apply"
              >
                {isApplying ? (
                  <Spinner size="sm" />
                ) : (
                  t('admin.library.bulkEdit.apply', { count: affectedCount })
                )}
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BulkEditTracksModal;
